import type { ReactNode } from "react";

export enum SourceType {
  Route = "route",
  Url = "url",
  Action = "action",
  // No navigation — used for nav items (e.g. My Queue's program-type
  // children) that should only persist a value (via Source.programType)
  // without triggering a route change, external link, or action.
  None = "none"
}

export enum NavItemType {
  Logo = "logo",
  Link = "link",
  Dropdown = "dropdown",
  Group = "group",
  Action = "action",
  ProfileMenu = "profileMenu"
}

export enum NavbarRendererVariant {
  Top = "top",
  Menu = "menu",
  Mobile = "mobile"
}

export enum DropdownVariant {
  Top = "top",
  Mobile = "mobile",
  Submenu = "submenu"
}

export enum GroupVariant {
  Dropdown = "dropdown",
  Mobile = "mobile"
}

export interface Source {
  type: SourceType;
  key: string;
  value: string;
  /**
   * Program type code (e.g. "LAP", "BL") for My Queue nav children — one
   * child per program type. When set, navigateFromSource persists it via
   * setProgramType before navigating.
   */
  programType?: string;
}

export interface Visibility {
  isVisible?: boolean;
}

export interface ThemeConfig {
  backgroundColor: string;
  textColor: string;
  activeTextColor: string;
  hoverTextColor: string;
  borderColor: string;
}

export interface Branding {
  clientName?: string;
  logo?: string;
  altText?: string;
}

export interface AvatarConfig {
  showInitials?: boolean;
}

export interface UserInfoConfig {
  showName?: boolean;
  showEmail?: boolean;
}

export interface NavItem {
  id: string;
  type: NavItemType;
  label?: string;
  position?: number;
  visible?: Visibility;
  source?: Source;
  children?: NavItem[];
  branding?: Branding;
  avatar?: AvatarConfig;
  userInfo?: UserInfoConfig;
  items?: NavItem[];
}

export interface NavigationConfig {
  themeConfig: ThemeConfig;
  topNav: NavItem[];
  profileMenu?: NavItem;
}

export interface NavbarUser {
  name?: string;
  email?: string;
}

export interface ConfigNavbarProps {
  config: NavigationConfig;
  user?: NavbarUser;
  executeAction?: (key: string) => void;
  className?: string;
}

export interface NavItemComponentProps {
  item: NavItem;
}

export interface NavbarRendererProps extends NavItemComponentProps {
  variant?: NavbarRendererVariant;
  onNavigate?: () => void;
}

export interface DropdownProps extends NavItemComponentProps {
  variant?: DropdownVariant;
  onNavigate?: () => void;
}

export interface GroupProps extends NavItemComponentProps {
  variant?: GroupVariant;
  onNavigate?: () => void;
}

export interface NavLinkProps extends NavItemComponentProps {
  variant?: NavbarRendererVariant;
  onNavigate?: () => void;
}

export interface NavbarContextValue {
  theme: ThemeConfig;
  user?: NavbarUser;
  navigateFromSource: (source?: Source) => void;
  isRouteActive: (source?: Source) => boolean;
}

export interface NavbarProviderProps {
  theme: ThemeConfig;
  user?: NavbarUser;
  executeAction?: (key: string) => void;
  children: ReactNode;
}
